/**
 * High-level save entry for the authorisation details review screen: builds
 * updated auth entries, posts the review dump, refetches auth-on-file from
 * Postgres, then applies review statuses on full J-code rows via UPDATE_J_CODES
 * so server-side dump merges are not overwritten.
 *
 * @module authorisationDetails/utils/saveAuthorisationDetailsReview
 */
import { updateCPTtables } from "../../../api/postCall/updateCPTtables";
import { singleOrderDetailsGenericCallForPostgres } from "../../../api/postgreSQL/postCalls/singleOrderDetailsGenericCallForPostgres";
import {
  AuthOnFile,
  JCodeDetails,
} from "../../../data-model/medicalPaOrdersModel";
import { SaveAuthorisationDetailsReviewParams } from "../../../data-model/saveAuthorizationDetailsReview";
import { SingleOrderDetailsTableName } from "../../../enums/singleOrderDetailsTableName";
import { getAuthLetterReviewStatusPerJCode } from "../../../utils/updateJcodesWithCorrectDocRef";
import { buildUpdatedAuthEntries } from "./buildAuthEntries";
import { getJCodeId, getJCodesByIds } from "./saveReviewHelpers";
import { submitReviewDump } from "./saveReviewPersistence";

/** Postgres single-table response wrapper for auth-on-file. */
interface AuthOnFileDetailsResponse {
  auth_on_file: AuthOnFile | null;
}

/**
 * Loads the latest auth-on-file J-codes for the order, then overlays
 * `auth_letter_review_status` so the update-J-codes payload carries full rows
 * (post-dump) instead of sparse objects that would clobber other fields.
 *
 * @param orderId - PA order id
 * @param orgId - Organization id
 * @param jCodeIds - J-code row ids included in this save (used when selecting fresh rows)
 * @param reviewStatusById - Target `auth_letter_review_status` per J-code id
 * @param fallbackJCodes - J-codes from the pre-dump merge if Postgres has not yet returned a row
 * @returns J-code objects ready for {@link updateCPTtables}
 */
async function buildMergedJCodesForReviewStatusSync(
  orderId: string,
  orgId: string,
  jCodeIds: string[],
  reviewStatusById: Map<string, string>,
  fallbackJCodes: JCodeDetails[],
): Promise<JCodeDetails[]> {
  const fresh =
    await singleOrderDetailsGenericCallForPostgres<AuthOnFileDetailsResponse>({
      order_id: orderId,
      org_id: orgId,
      table_name: SingleOrderDetailsTableName.AUTH_ON_FILE,
    });
  const freshEntries =
    fresh.auth_on_file?.entries ?? fresh.auth_on_file?.auth_entries ?? [];
  const freshById = new Map(
    getJCodesByIds(freshEntries, jCodeIds).map((j) => [getJCodeId(j), j]),
  );
  const fallbackById = new Map(fallbackJCodes.map((j) => [getJCodeId(j), j]));
  const out: JCodeDetails[] = [];
  for (const id of reviewStatusById.keys()) {
    const status = reviewStatusById.get(id);
    if (status === undefined) continue;
    const base = freshById.get(id) ?? fallbackById.get(id);
    if (base) {
      out.push({ ...base, auth_letter_review_status: status });
    }
  }
  return out;
}

/**
 * Orchestrates the full save flow: merge edits → post review dump → refetch
 * auth-on-file from Postgres → sync `auth_letter_review_status` via update-j-codes
 * on full J-code rows (avoids sparse payloads overwriting post-dump data).
 *
 * @param params - Save callback bundle from the review UI
 * @param params.orderId - PA order id
 * @param params.patientId - Patient identifier (MRN) required by submit-auth-letter-entry
 * @param params.orgId - Organization id for the review dump API
 * @param params.authOnFile - Auth-on-file payload (must include `entries` or `auth_entries` when saving)
 * @param params.jCodeIds - J-codes included in this save; no-op when empty
 * @param params.procedureFieldStateMap - Optional procedure-level field overrides per J-code
 * @param params.basicInfoFieldState - Optional shared auth / member / provider fields
 * @param params.diagnosisDetailFieldState - Optional primary diagnosis rows for persistence
 * @param params.procedureStatuses - Optional approval map for the review dump payload
 * @param params.primaryPlanNameFallback - Primary plan name when basic-info state omits it
 * @param params.onSuccess - Called after dump, CPT/J-code status sync, and related persistence succeed
 * @param params.onError - Called with the caught error if any step fails
 * @returns Resolves when complete; resolves immediately when `orderId` or `jCodeIds` is missing
 */
export const saveAuthorisationDetailsReview = async ({
  orderId,
  patientId,
  orgId,
  authOnFile,
  jCodeIds,
  fetchAll,
  procedureFieldStateMap,
  basicInfoFieldState,
  diagnosisDetailFieldState,
  basicInfoStatus,
  diagnosisDetailStatus,
  procedureStatuses,
  primaryPlanNameFallback,
  onSuccess,
  onError,
}: SaveAuthorisationDetailsReviewParams): Promise<void> => {
  if (!orderId?.trim() || jCodeIds.length === 0) return;

  const authEntries = authOnFile?.entries ?? authOnFile?.auth_entries ?? [];

  const updatedAuthEntries = buildUpdatedAuthEntries(
    authEntries,
    jCodeIds,
    procedureFieldStateMap,
    basicInfoFieldState,
    diagnosisDetailFieldState,
    basicInfoStatus,
    diagnosisDetailStatus,
    procedureStatuses,
  );
  const mergedJCodes = getJCodesByIds(updatedAuthEntries, jCodeIds);
  const idSet = new Set(jCodeIds);
  const reviewStatusRows = getAuthLetterReviewStatusPerJCode(
    updatedAuthEntries,
  ).filter((row) => idSet.has(row.id));
  const jCodesForReviewStatusSync: JCodeDetails[] = reviewStatusRows.map(
    (row) => ({
      id: row.id,
      auth_letter_review_status: row.auth_letter_review_status,
    }),
  );

  try {
    await submitReviewDump(
      orderId,
      patientId,
      orgId,
      "",
      mergedJCodes,
      basicInfoFieldState,
      basicInfoStatus,
      diagnosisDetailStatus,
      procedureStatuses,
      procedureFieldStateMap,
    );
    if (jCodesForReviewStatusSync.length > 0) {
      const reviewStatusById = new Map<string, string>();
      for (const j of jCodesForReviewStatusSync) {
        const id = j.id?.trim();
        if (!id) continue;
        reviewStatusById.set(id, j.auth_letter_review_status ?? "");
      }
      const jCodesForUpdate = await buildMergedJCodesForReviewStatusSync(
        orderId,
        orgId,
        jCodeIds,
        reviewStatusById,
        mergedJCodes,
      );
      if (jCodesForUpdate.length > 0) {
        await updateCPTtables({
          order_id: orderId,
          org_id: orgId,
          j_codes: jCodesForUpdate,
        });
      }
    }
    await fetchAll?.(orderId, orgId, { force: true });
    onSuccess?.();
  } catch (error) {
    onError?.(error);
  }
};
